import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import {
    SettingType,
    BlockType,
    Define,
    ValidationMessage,
    ValidationMessageType,
    Setting,
    CodeBlock,
    SettingState,
    TibboDataType,
    SettingControlType,
} from '@appblocks/codegeneration/types';
import PlatformUtils, {
    TIBBO_DEVICE_ID,
} from '@appblocks/platforms/PlatformUtils';
import LUISGroup from '@appblocks/codegeneration/SettingsConfigurations/LUISGroup';
import * as simulator from './simulator';
import * as tios from './tios';

export default class LCD extends BaseFeature {
    constructor() {
        super();
        this.name = 'LCD for TPP2';
        this.id = 'lcd';
        this.icon = 'CgScreen';
        this.priority = 101;
        this.requirements = [['TPP2W(G2)', 'TPP2W(G3)'], 'TPP'];
        const settings: Array<Setting> = [
            {
                id: 'GENERAL',
                name: 'General',
                settingType: SettingType.GROUP,
                default: '',
            },
            {
                id: 'IMAGES_GROUP',
                name: 'Images',
                settingType: SettingType.GROUP,
                default: '',
            },
            {
                id: 'SETTINGS',
                name: 'Settings',
                settingType: SettingType.GROUP,
                default: '',
            },
            {
                id: 'ENABLED',
                name: 'LCD Enabled',
                settingType: SettingType.OPTION,
                group: 'GENERAL',
                required: true,
                default: '0',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'BACKCOLOR',
                name: 'Background Color',
                settingType: SettingType.COLOR,
                group: 'GENERAL',
                default: '#000000',
                required: true,
            },
            {
                id: 'FORECOLOR',
                name: 'Foreground Color',
                settingType: SettingType.COLOR,
                group: 'GENERAL',
                default: '#ffffff',
                required: true,
            },
            {
                id: 'LOGFONT',
                name: 'Log Font Size',
                settingType: SettingType.OPTION,
                group: 'GENERAL',
                required: true,
                default: 'LCD_SMALL_FONT',
                options: [
                    {
                        name: 'Standard Font',
                        value: 'LCD_STD_FONT',
                    },
                    {
                        name: 'Small Font',
                        value: 'LCD_SMALL_FONT',
                    },
                ],
            },
            {
                id: 'WIDGETS',
                name: 'LCD Widgets',
                settingType: SettingType.ARRAY,
                required: true,
                group: 'GENERAL',
                controlType: SettingControlType.WIDGETS,
                default: new Array<BaseFeature>(0),
            },
            {
                id: 'SCREENS',
                name: 'Screens',
                settingType: SettingType.ARRAY,
                required: true,
                group: 'GENERAL',
                refId: 'display_screen',
                default: new Array<BaseFeature>(0),
            },
            {
                id: 'IMAGES',
                name: 'Images',
                settingType: SettingType.ARRAY,
                refId: 'lcd_image',
                required: true,
                group: 'IMAGES_GROUP',
                default: new Array<BaseFeature>(0),
            },
            {
                id: 'LCD_SCREEN_WIDGET_COLUMNS',
                name: 'LCD Screen Widget Columns',
                settingType: SettingType.HIDDEN,
                required: true,
                default: 320,
            },
            {
                id: 'LCD_SCREEN_WIDGET_ROWS',
                name: 'LCD Screen Widget Rows',
                settingType: SettingType.HIDDEN,
                required: true,
                default: 240,
            },
            {
                id: 'GROUPS',
                name: 'Settings Groups',
                settingType: SettingType.ARRAY,
                refId: 'luis_group',
                group: 'SETTINGS',
                required: true,
                controlType: SettingControlType.TABS,
                default: [
                    new LUISGroup(),
                ],
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getSettingsEnabled(): boolean {
        const features = this.project.features;
        let enterLCDNodeFound = false;
        for (let i = 0; i < features.length; i++) {
            if (features[i].id === 'node_lcd_menu') {
                enterLCDNodeFound = true;
                break;
            }
        }
        return enterLCDNodeFound;
    }

    getState(settingId: string): SettingState {
        switch (settingId) {
            case 'ENABLED':
                return SettingState.ENABLED;
            case 'WIDGETS':
                return SettingState.HIDDEN;
            default:
                if (!this.getEnabled()) {
                    return SettingState.DISABLED;
                }
                return SettingState.ENABLED;
        }
    }

    getDependencies() {
        const dependencies = [];
        if (this.getSettingsEnabled()) {
            dependencies.push('stg');
            dependencies.push('kp');
        }
        if (this.getEnabled()) {
            dependencies.push('display_lib');
        }

        return dependencies;
    }

    getFiles(): Array<string> {
        let { files } = this;

        files = files.concat([
            '11x19(HP).bin',
            'Tibbo-5x7(V).bin',
        ]);
        if (this.getSettingsEnabled()) {
            files = files.concat([
                'libraries/ui/ui_input_box.tbh',
                'libraries/ui/ui_input_box.tbs',
                'libraries/ui/ui_list_box.tbh',
                'libraries/ui/ui_list_box.tbs',
                'libraries/ui/ui_msg_box.tbh',
                'libraries/ui/ui_msg_box.tbs',
                'libraries/ui/ui_menu.tbh',
                'libraries/ui/ui_menu.tbs',
                'libraries/ui/ui.tbh',
                'libraries/ui/ui.tbs',
                'menu.xtxt',
                'icon.bmp',
            ]);
        }
        return files;
    }

    getDefines(): Array<Define> {
        const defines: Array<Define> = [];

        defines.push({
            name: 'LCD_STD_FONT',
            value: '"11X19(HP).bin"',
        });
        defines.push({
            name: 'LCD_SMALL_FONT',
            value: '"Tibbo-5x7(V).bin"',
        });

        return defines;
    }

    validate(): Array<ValidationMessage> {
        const errors: ValidationMessage[] = [];
        const boardSettings = this.project.getFeature('boardsetting');
        const isEnabled = this.getEnabled();
        const GROUPS = this.getSettingValue('GROUPS');
        const LCDMenuFeatureEnabled = this.getSettingsEnabled();
        if (LCDMenuFeatureEnabled && GROUPS.length > 0) {
            for (let i = 0; i < GROUPS.length; i++) {
                const SETTINGS = GROUPS[i].getSettingValue('SETTINGS');
                if (SETTINGS.length === 0) {
                    errors.push({
                        featureId: GROUPS[i].getID(),
                        settingId: 'SETTINGS',
                        messageType: ValidationMessageType.ERROR,
                        message: 'LCD settings groups must have at least one setting.',
                    });
                }
            }
        }
        if (isEnabled && boardSettings.getSettingValue('TPB') !== 'tpb2lg2') {
            errors.push({
                messageType: ValidationMessageType.UPDATE,
                message: 'LCD enabled',
                featureId: boardSettings.getID(),
                settingId: 'TPB',
                data: 'tpb2lg2',
            });
        }
        if (!isEnabled && boardSettings.getSettingValue('TPB') === 'tpb2lg2') {
            errors.push({
                messageType: ValidationMessageType.UPDATE,
                message: 'LCD enabled',
                featureId: boardSettings.getID(),
                settingId: 'TPB',
                data: 'true',
            });
        }

        return errors;
    }

    getCodeBlocks(): CodeBlock[] {
        if (this.project.runtime === 'simulator') {
            return simulator.getCodeBlocks(this);
        }
        return tios.getCodeBlocks(this);
    }

    getDetails(): string {
        return 'https://docs.tibbo.com/taiko/object_lcd';
    }
}
